import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@/lib/auth-context';
import { useTeacherCredits } from '@/hooks/useTeacherCredits';
import type { AiTool, AiToolsCatalogResponse } from '@/types/aiTools';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
const AI_SLOT_LIMIT = 4;
const GEC0_SLOT_LIMIT = 2;

type ToolsByType = {
  ai: AiTool[];
  gec0: AiTool[];
};

type ActiveToolIds = {
  ai: string[];
  gec0: string[];
};

export default function TeacherAiMarketplace() {
  const { session } = useAuthContext() as { session: { user?: { id: string }; access_token?: string } | null };
  const teacherId = session?.user?.id ?? null;
  const accessToken = session?.access_token ?? null;

  const { loading: creditsLoading, available: creditsAvailable } = useTeacherCredits(teacherId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [allTools, setAllTools] = useState<AiTool[]>([]);
  const [activeSlots, setActiveSlots] = useState<Record<string, string | null>>({});
  const [togglingTool, setTogglingTool] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<'all' | 'ai' | 'gec0'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const loadCatalog = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${CORE_API}/api/v1/marketplace/ai-tools?role=teacher`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load tools (${res.status})`);
      }
      const data: AiToolsCatalogResponse = await res.json();
      setAllTools(data.tools || []);
      setActiveSlots(data.active_slots || {});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load AI tools';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // Categorize tools by type
  const toolsByType: ToolsByType = useMemo(() => {
    const ai: AiTool[] = [];
    const gec0: AiTool[] = [];
    for (const tool of allTools) {
      const slotType = tool.meta?.slot_type || (tool.meta?.category?.toLowerCase().includes('gec0') ? 'gec0' : 'ai');
      if (slotType === 'gec0') {
        gec0.push(tool);
      } else {
        ai.push(tool);
      }
    }
    return { ai, gec0 };
  }, [allTools]);

  // Get active tool IDs by type
  const activeToolIds: ActiveToolIds = useMemo(() => {
    const ai: string[] = [];
    const gec0: string[] = [];
    for (const [slotId, toolId] of Object.entries(activeSlots)) {
      if (!toolId) continue;
      if (slotId.startsWith('gec0_')) {
        gec0.push(toolId);
      } else {
        ai.push(toolId);
      }
    }
    return { ai, gec0 };
  }, [activeSlots]);

  // Filter tools for display
  const filteredTools = useMemo(() => {
    let tools = allTools;

    // Filter by category
    if (filterCategory === 'ai') {
      tools = toolsByType.ai;
    } else if (filterCategory === 'gec0') {
      tools = toolsByType.gec0;
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      tools = tools.filter((t) => {
        const label = (t.meta?.label || '').toLowerCase();
        const description = (t.meta?.description || '').toLowerCase();
        const summary = (t.meta?.summary || '').toLowerCase();
        return label.includes(query) || description.includes(query) || summary.includes(query);
      });
    }

    return tools;
  }, [allTools, toolsByType, filterCategory, searchQuery]);

  const isToolActive = (toolId: string): boolean => {
    return Object.values(activeSlots).includes(toolId);
  };

  const getToolSlotType = (tool: AiTool): 'ai' | 'gec0' => {
    return tool.meta?.slot_type === 'gec0' || tool.meta?.category?.toLowerCase().includes('gec0') ? 'gec0' : 'ai';
  };

  const canAddTool = (tool: AiTool): boolean => {
    const slotType = getToolSlotType(tool);
    if (slotType === 'gec0') {
      return activeToolIds.gec0.length < GEC0_SLOT_LIMIT;
    }
    return activeToolIds.ai.length < AI_SLOT_LIMIT;
  };

  const findAvailableSlot = (tool: AiTool): string | null => {
    const slotType = getToolSlotType(tool);
    const limit = slotType === 'gec0' ? GEC0_SLOT_LIMIT : AI_SLOT_LIMIT;
    const prefix = slotType === 'gec0' ? 'gec0_slot_' : 'ai_slot_';

    for (let i = 1; i <= limit; i++) {
      const slotId = `${prefix}${i}`;
      if (!activeSlots[slotId]) {
        return slotId;
      }
    }
    return null;
  };

  const findSlotWithTool = (toolId: string): string | null => {
    for (const [slotId, tid] of Object.entries(activeSlots)) {
      if (tid === toolId) return slotId;
    }
    return null;
  };

  const toggleTool = async (tool: AiTool) => {
    if (!accessToken) return;

    setTogglingTool(tool.id);
    setError(null);
    setSuccessMsg(null);

    const isActive = isToolActive(tool.id);

    try {
      if (isActive) {
        // Remove tool
        const slotId = findSlotWithTool(tool.id);
        if (!slotId) throw new Error('Could not find slot for tool');

        const res = await fetch(`${CORE_API}/api/v1/marketplace/ai-tools/unassign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ role: 'teacher', slot: slotId }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to remove tool');

        setActiveSlots((prev) => {
          const next = { ...prev };
          delete next[slotId];
          return next;
        });
        setSuccessMsg(`${tool.meta?.label || 'Tool'} removed from your active tools`);
      } else {
        // Add tool
        if (!canAddTool(tool)) {
          const slotType = getToolSlotType(tool);
          const limit = slotType === 'gec0' ? GEC0_SLOT_LIMIT : AI_SLOT_LIMIT;
          throw new Error(`You've reached the ${slotType.toUpperCase()} tool limit (${limit}). Remove a tool first.`);
        }

        const slotId = findAvailableSlot(tool);
        if (!slotId) throw new Error('No available slot');

        const res = await fetch(`${CORE_API}/api/v1/marketplace/ai-tools/assign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ role: 'teacher', slot: slotId, tool_id: tool.id }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to add tool');

        setActiveSlots((prev) => ({ ...prev, [slotId]: tool.id }));
        setSuccessMsg(`${tool.meta?.label || 'Tool'} added to your active tools`);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to toggle tool';
      setError(message);
    } finally {
      setTogglingTool(null);
    }
  };

  // Auto-dismiss success message
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  if (loading) {
    return (
      <div className="space-y-6 p-4">
        <h1 className="text-2xl font-semibold text-cocoa">AI Tools Marketplace</h1>
        <div className="text-sm text-cocoa/70">Loading available tools...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-cocoa">AI Tools Marketplace</h1>
        <p className="mt-1 text-sm text-cocoa/70">
          Browse and activate tools to enhance your teaching workflow. Active tools appear on your Tools page.
        </p>
      </div>

      {/* Status Bar */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl bg-slate-50 p-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div>
            <span className="text-sm font-medium text-cocoa">AI Tools</span>
            <span className="ml-2 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
              {activeToolIds.ai.length} / {AI_SLOT_LIMIT}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <span className="text-sm font-medium text-cocoa">SmartBlock Trackers</span>
            <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
              {activeToolIds.gec0.length} / {GEC0_SLOT_LIMIT}
            </span>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <span className="text-sm font-medium text-cocoa">Credits</span>
            <span className="ml-2 text-sm font-semibold text-amber-700">
              {creditsLoading ? '...' : creditsAvailable}
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-lg border border-brick/30 bg-brick/10 p-3 text-sm text-brick">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700">
          {successMsg}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
          <button
            onClick={() => setFilterCategory('all')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filterCategory === 'all'
                ? 'bg-slate-100 text-cocoa'
                : 'text-slate-500 hover:text-cocoa'
            }`}
          >
            All Tools
          </button>
          <button
            onClick={() => setFilterCategory('ai')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filterCategory === 'ai'
                ? 'bg-sky-100 text-sky-800'
                : 'text-slate-500 hover:text-cocoa'
            }`}
          >
            AI ({toolsByType.ai.length})
          </button>
          <button
            onClick={() => setFilterCategory('gec0')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filterCategory === 'gec0'
                ? 'bg-emerald-100 text-emerald-800'
                : 'text-slate-500 hover:text-cocoa'
            }`}
          >
            SmartBlocks ({toolsByType.gec0.length})
          </button>
        </div>

        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2 pl-10 text-sm focus:border-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-100"
          />
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Tools Catalog */}
      {filteredTools.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <div className="text-slate-400">
            {searchQuery ? 'No tools match your search' : 'No tools available in this category'}
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredTools.map((tool) => {
            const active = isToolActive(tool.id);
            const slotType = getToolSlotType(tool);
            const isToggling = togglingTool === tool.id;
            const canAdd = canAddTool(tool);

            return (
              <ToolCatalogCard
                key={tool.id}
                tool={tool}
                slotType={slotType}
                isActive={active}
                isToggling={isToggling}
                canAdd={canAdd}
                onToggle={() => toggleTool(tool)}
              />
            );
          })}
        </div>
      )}

      {/* Link to Tools Page */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
        <p className="text-sm text-cocoa/70">
          Active tools are available on your{' '}
          <a href="/portal/teacher/tools" className="font-medium text-sky-600 hover:underline">
            Tools page
          </a>
        </p>
      </div>
    </div>
  );
}

type ToolCatalogCardProps = {
  tool: AiTool;
  slotType: 'ai' | 'gec0';
  isActive: boolean;
  isToggling: boolean;
  canAdd: boolean;
  onToggle: () => void;
};

function ToolCatalogCard({ tool, slotType, isActive, isToggling, canAdd, onToggle }: ToolCatalogCardProps) {
  const accentColor = slotType === 'gec0' ? 'emerald' : 'sky';
  const borderClass = isActive
    ? `border-${accentColor}-400 bg-${accentColor}-50`
    : 'border-slate-200 bg-white';

  return (
    <div
      className={`rounded-xl border-2 p-4 transition-all hover:shadow-md ${
        isActive
          ? slotType === 'gec0'
            ? 'border-emerald-400 bg-emerald-50'
            : 'border-sky-400 bg-sky-50'
          : 'border-slate-200 bg-white'
      }`}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-3">
          {tool.meta?.icon_url ? (
            <img src={tool.meta.icon_url} alt="" className="h-12 w-12 rounded-xl object-cover shadow-sm" />
          ) : (
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                slotType === 'gec0' ? 'bg-emerald-200 text-emerald-800' : 'bg-sky-200 text-sky-800'
              }`}
            >
              <span className="text-sm font-bold">{slotType === 'gec0' ? 'SB' : 'AI'}</span>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-cocoa leading-tight">
              {tool.meta?.label || tool.meta?.summary || tool.id}
            </h3>
            <span
              className={`inline-block mt-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                slotType === 'gec0'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-sky-100 text-sky-700'
              }`}
            >
              {slotType === 'gec0' ? 'SmartBlock' : 'AI Tool'}
            </span>
          </div>
        </div>
        {isActive && (
          <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-xs font-medium text-white">
            Active
          </span>
        )}
      </div>

      {/* Description */}
      <p className="mb-3 text-sm text-cocoa/70 line-clamp-2">
        {tool.meta?.description || tool.meta?.surface_hint || 'Powerful tool for your classroom'}
      </p>

      {/* Surface hint */}
      {tool.meta?.surface_hint && tool.meta.description && (
        <p className="mb-3 text-xs italic text-slate-500">"{tool.meta.surface_hint}"</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {(tool.tc_cost ?? 0) > 0 ? (
            <>
              <svg className="h-4 w-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-amber-700">{tool.tc_cost} TC</span>
            </>
          ) : (
            <span className="text-sm font-medium text-emerald-600">Free</span>
          )}
        </div>

        <button
          onClick={onToggle}
          disabled={isToggling || (!isActive && !canAdd)}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            isActive
              ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              : slotType === 'gec0'
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : 'bg-sky-600 text-white hover:bg-sky-700'
          }`}
        >
          {isToggling ? (
            <span className="flex items-center gap-1">
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {isActive ? 'Removing...' : 'Adding...'}
            </span>
          ) : isActive ? (
            'Remove'
          ) : !canAdd ? (
            'Slots Full'
          ) : (
            '+ Add'
          )}
        </button>
      </div>
    </div>
  );
}
